import { useEffect, useState } from "react";
import { useGetCredentials } from "../../../helpers/queries/credentials/useGetCredentials";
import { formatFullDate, formatTime } from "../../../lib/utils";
import AddCredentialModal from "./AddCredentialModal";
import { Spinner } from "../../../components/Spinner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { deleteCredential } from "../../../helpers/mutations/credentials";
import { Button } from "../../../components/ui/button";
import { FiCopy, FiKey, FiTrash2 } from "react-icons/fi";
import { FaCheckCircle } from "react-icons/fa";
import DeleteModal from "../../../components/modals/DeleteModal";
import empty from "../../../assets/images/empty_credentials.png";
import { useGetUserData } from "../../../helpers/queries/user/useGetUserData";
import { usePaginationSync } from "../../../hooks/usePaginationSync";
import { PaginationComponent } from "../../../components/Pagination";
import DataTable from "../../../components/DataTable";

const Credentials = () => {
  const { data: user } = useGetUserData();
  const role = user?.user?.role;
  const apiKey = user?.user?.api_key;
  const [lastPage, setLastPage] = useState(1);
  const { currentPage, updatePage } = usePaginationSync(lastPage);
  const { isLoading, isSuccess, isError, data } =
    useGetCredentials(currentPage);

  useEffect(() => {
    setLastPage(data?.data?.last_page);
  }, [data]);

  const [openDeleteModal, setOpenDeleteModal] = useState(null);
  const handleDeleteModal = () => setOpenDeleteModal(null);

  const queryClient = useQueryClient();
  const { mutate: deleteMutation, isPending } = useMutation({
    mutationFn: (id) => deleteCredential(id),
    onSuccess: () => {
      toast("Credential deleted successfully");
      handleDeleteModal();
      queryClient.invalidateQueries({ queryKey: ["credentials"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete credential");
    },
  });

  const handleDelete = (id) => deleteMutation(id);

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 5000); // Revert after 5 seconds
  };

  const columns = [
    {
      accessorKey: "name",
      header: () => <span>Name</span>,
      cell: ({ row }) => <span>{row.original.name}</span>,
    },
    {
      accessorKey: "type",
      header: () => <span>Type</span>,
      cell: ({ row }) => <span>{row.original.type}</span>,
    },
    {
      accessorKey: "created_at",
      accessorFn: (row) =>
        formatFullDate(row.created_at) + " " + formatTime(row.created_at),
      header: () => <span>Created</span>,
      cell: ({ row }) => (
        <span>
          <span className="text-neutral800">
            {formatFullDate(row.original.created_at)},
          </span>{" "}
          <span className="text-neutral600">
            {formatTime(row.original.created_at)}
          </span>
        </span>
      ),
    },
    ...(role && role !== "Viewer"
      ? [
          {
            id: "action",
            header: () => <span>Action</span>,
            width: "10%",
            cell: ({ row }) => {
              const item = row.original;
              const isDeleteModalOpen = openDeleteModal === item.id;
              return (
                <span>
                  <DeleteModal
                    onOpenChange={(open) => {
                      open
                        ? setOpenDeleteModal(item?.id)
                        : setOpenDeleteModal(null);
                    }}
                    open={isDeleteModalOpen}
                    data={item?.name}
                    id={item?.id}
                    title="Delete credential"
                    handleDelete={handleDelete}
                    loading={isPending}
                  >
                    <Button
                      className="w-fit bg-[#FF44441A] hover:bg-[#ffdada] text-[#E12A5B]"
                      variant={"ghost"}
                    >
                      <FiTrash2 size={20} />
                    </Button>
                  </DeleteModal>
                </span>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="w-full">
        <div className=" w-full lg:block flex justify-between items-center py-6 lg:px-10 md:px-5 px-4 bg-white border-b ">
          <div className="w-full flex lg:flex-row flex-col justify-between lg:items-center gap-4 mb-1">
            <div className="flex gap-3 items-center">
              <div>
                <div className="w-[50px] h-[50px] rounded-full border border-neutral300 flex justify-center items-center">
                  <FiKey className="text-neutral700 text-[20px]" />
                </div>
              </div>
              <div>
                <p className="text-darkGrey font-semibold md:block hidden">
                  Credentials
                </p>
                <p className="text-neutral600 md:text-base text-sm">
                  Securely store and manage login information related to your
                  assets.
                </p>
              </div>
            </div>

            {role && role !== "Viewer" && (
              <div className="flex md:flex-row flex-col-reverse gap-4 md:items-center">
                <Button
                  className="bg-neutral200 text-neutral800"
                  variant="outline"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <FaCheckCircle size={16} className="text-green-600" />
                  ) : (
                    <FiCopy size={16} />
                  )}{" "}
                  Copy API Key
                </Button>
                <AddCredentialModal />
              </div>
            )}
          </div>
        </div>

        {isLoading && !isSuccess && (
          <div className="h-[55vh] w-full flex items-center justify-center">
            <Spinner />
          </div>
        )}

        {isError && !isLoading && (
          <div className="h-[45vh] w-full flex justify-center flex-col items-center">
            <p className="font-semibold text-xl text-center">
              There was an error getting the data
            </p>
            <p className="text-center">{data?.message ?? ""}</p>
          </div>
        )}

        {!isLoading && isSuccess && data?.data?.data?.length > 0 && (
          <div className="lg:p-10 md:p-5 p-4 bg-white h-[100%]">
            <DataTable columns={columns} data={data?.data?.data} />
            <PaginationComponent
              lastPage={data?.data?.last_page}
              currentPage={currentPage}
              handlePageChange={updatePage}
            />
          </div>
        )}

        {data?.data?.data?.length === 0 && !isLoading && isSuccess && (
          <div className="h-[45vh] mt-[6rem] w-full flex justify-center flex-col items-center text-center p-4">
            <img src={empty} alt="empty" />
            <p className=" font-semibold mt-4 text-darkGrey">
              No credential added yet
            </p>
            <p className="text-neutral600 text-sm max-w-[360px] my-3">
              Securely store login information related to your assets. This
              helps with authenticated scans and access control during
              monitoring.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default Credentials;
